import { useState } from 'react';
import { Draft, produce } from 'immer';
import { v4 as uuidv4 } from 'uuid';
import { OptionGroupType } from 'src/constants';

type Strategy = Models.IngestConfigApi.ForcedRankings.ForcedRankingStrategy;
type Ranking = Models.IngestConfigApi.ForcedRankings.Ranking;
type PartialProductSpec = Models.IngestConfigApi.ForcedRankings.PartialProductSpec;

interface UseStrategyReturn {
    strategy: Strategy | null;
    setStrategy: (strategy: Strategy) => void;
    updateReferenceProduct: (referenceProduct: string) => void;
    updateName: (name: string) => void;
    updateRanking: (variantIndex: number, rankingIndex: number, update: Partial<Ranking>) => void;
    addRank: (variantIndex: number, newRank: Ranking) => void;
    deleteRank: (variantIndex: number, designConceptId: string) => void;
    updateVariantCultures: (variantIndex: number, cultures: string[]) => void;
    addVariant: () => void;
    deleteVariant: (variantIndex: number) => void;
    addOptionGroup: (groupType: OptionGroupType, id: string, spec: PartialProductSpec) => void;
    updateOptionGroup: (groupType: OptionGroupType, id: string, update: PartialProductSpec) => void;
    deleteOptionGroup: (groupType: OptionGroupType, id: string) => void;
    hasUnsavedChanges: boolean;
    discardChanges: () => void;
    markAsSaved: () => void;
    importRanksFromCsv: (variantIndex: number, csvContent: string) => Promise<void>;
    addRankToVariant: (variantIndex: number, ranking: Ranking) => void;
    deleteRankFromVariant: (variantIndex: number, ranking: Ranking) => void;
    reorderRankings: (variantIndex: number, oldIndex: number, newIndex: number) => void;
    cloneVariant: (variantIndex: number) => void;
    updateBulkFilteringOptionGroup: (
        variantIndex: number,
        rankings: Ranking[],
        specId: string | null
    ) => void;
    updateBulkDefaultOptionGroup: (
        variantIndex: number,
        rankings: Ranking[],
        specId: string | null
    ) => void;
    deleteBulkRankings: (variantIndex: number, rankings: Ranking[]) => void;
}

const updateRanksByIndex = (rankings: Ranking[]): void => {
    rankings.forEach((ranking, index) => {
        // eslint-disable-next-line no-param-reassign
        ranking.rank = index + 1;
    });
};

const areRankingsEqual = (ranking1: Ranking, ranking2: Ranking):
boolean => ranking1.designConceptId === ranking2.designConceptId
        && ranking1.filteringProductSpecId === ranking2.filteringProductSpecId
        && ranking1.defaultProductSpecId === ranking2.defaultProductSpecId
        && ranking1.rank === ranking2.rank;

export function useStrategy(initialStrategy: Strategy): UseStrategyReturn {
    const [strategy, setStrategyState] = useState<Strategy | null>(initialStrategy);
    const [original, setOriginal] = useState<Strategy | null>(initialStrategy);

    const setStrategy = (newStrategy: Strategy): void => {
        setStrategyState(newStrategy);
        setOriginal(newStrategy);
    };

    const updateReferenceProduct = (referenceProduct: string): void => {
        setStrategyState((prev) => (prev ? { ...prev, referenceProduct } : null));
    };

    const updateName = (name: string): void => {
        setStrategyState((prev) => (prev ? { ...prev, name } : null));
    };

    const updateRanking = (
        variantIndex: number,
        rankingIndex: number,
        update: Partial<Ranking>,
    ): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (!variant) {
                    console.error(`Variant not found at index ${variantIndex}`);
                    return;
                }

                if (rankingIndex < 0 || rankingIndex >= variant.rankings.length) {
                    console.error(`Ranking index ${rankingIndex} out of bounds`);
                    return;
                }

                variant.rankings[rankingIndex] = { ...variant.rankings[rankingIndex], ...update };
            });
        });
    };

    const addRank = (variantIndex: number, newRank: Ranking): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) variant.rankings.push(newRank);
            });
        });
    };

    const deleteRank = (variantIndex: number, designConceptId: string): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = variant.rankings.filter((r: Ranking) => r.designConceptId !== designConceptId);
                }
            });
        });
    };

    const updateVariantCultures = (variantIndex: number, cultures: string[]): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) variant.cultures = cultures;
            });
        });
    };

    const addVariant = (): void => {
        setStrategyState((prev) => {
            if (!prev) return null;

            const newId = uuidv4();

            return produce(prev, (draft: Draft<Strategy>) => {
                draft.cultureVariantRankings.push({ name: newId, cultures: [], rankings: [] });
            });
        });
    };

    const deleteVariant = (variantIndex: number): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                draft.cultureVariantRankings.splice(variantIndex, 1);
            });
        });
    };

    const updateOptionGroup = (
        groupType: OptionGroupType,
        id: string,
        update: PartialProductSpec,
    ): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const target = groupType === OptionGroupType.FILTERING
                    ? draft.filteringPartialProductSpecs
                    : draft.defaultPartialProductSpecs;

                if (target[id]) {
                    target[id] = { ...target[id], ...update };
                }
            });
        });
    };

    const addOptionGroup = (
        groupType: OptionGroupType,
        id: string,
        spec: PartialProductSpec,
    ): void => {
        setStrategyState((prev) => {
            if (!prev) return null;

            return produce(prev, (draft: Draft<Strategy>) => {
                const specs = groupType === OptionGroupType.FILTERING
                    ? draft.filteringPartialProductSpecs ?? {}
                    : draft.defaultPartialProductSpecs ?? {};

                if (groupType === OptionGroupType.FILTERING) {
                    // eslint-disable-next-line no-param-reassign
                    draft.filteringPartialProductSpecs = { ...specs, [id]: spec };
                } else {
                    // eslint-disable-next-line no-param-reassign
                    draft.defaultPartialProductSpecs = { ...specs, [id]: spec };
                }
            });
        });
    };

    const deleteOptionGroup = (groupType: OptionGroupType, id: string): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const target = groupType === OptionGroupType.FILTERING
                    ? draft.filteringPartialProductSpecs
                    : draft.defaultPartialProductSpecs;

                delete target[id];

                // Update any rankings that were using this option group
                draft.cultureVariantRankings?.forEach((variant, variantIndex) => {
                    // eslint-disable-next-line no-param-reassign
                    draft.cultureVariantRankings[variantIndex].rankings = variant.rankings.map((ranking) => {
                        if (groupType === OptionGroupType.FILTERING && ranking.filteringProductSpecId === id) {
                            return { ...ranking, filteringProductSpecId: null };
                        }
                        if (groupType === OptionGroupType.DEFAULT && ranking.defaultProductSpecId === id) {
                            return { ...ranking, defaultProductSpecId: null };
                        }
                        return ranking;
                    });
                });
            });
        });
    };

    const hasUnsavedChanges = strategy && original ? JSON.stringify(strategy) !== JSON.stringify(original) : false;

    const discardChanges = (): void => {
        if (original) setStrategyState(original);
    };

    const markAsSaved = (): void => {
        if (strategy) setOriginal(strategy);
    };

    const importRanksFromCsv = async (variantIndex: number, csvContent: string): Promise<void> => {
        if (!strategy) return;

        const rows = csvContent.split('\n').map((row) => row.split(',').map((cell) => cell.trim()));

        const headers = rows[0];
        const expectedHeaders = ['Design Concept ID'];

        if (!expectedHeaders.every((header, index) => headers[index] === header)) {
            throw new Error('Invalid CSV format. Expected header: Design Concept ID');
        }

        const newRankings: Ranking[] = [];

        for (let i = 1; i < rows.length; i++) {
            const [designConceptId] = rows[i];

            if (designConceptId) {
                newRankings.push({
                    rank: newRankings.length + 1,
                    designConceptId,
                    filteringProductSpecId: null,
                    defaultProductSpecId: null,
                });
            }
        }

        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = newRankings;
                }
            });
        });
    };

    const addRankToVariant = (variantIndex: number, ranking: Ranking): void => {
        setStrategyState((prev) => {
            if (!prev) return prev;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    const targetIndex = Math.min(ranking.rank - 1, variant.rankings.length);

                    variant.rankings.splice(targetIndex, 0, ranking);

                    updateRanksByIndex(variant.rankings);
                }
            });
        });
    };

    const reorderRankings = (variantIndex: number, oldIndex: number, newIndex: number): void => {
        setStrategyState((prev) => {
            if (!prev) return prev;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant && variant.rankings.length > 0) {
                    const [movedItem] = variant.rankings.splice(oldIndex, 1);

                    variant.rankings.splice(newIndex, 0, movedItem);

                    updateRanksByIndex(variant.rankings);
                }
            });
        });
    };

    const deleteRankFromVariant = (variantIndex: number, ranking: Ranking): void => {
        setStrategyState((prev) => {
            if (!prev) return prev;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = variant.rankings
                        .filter((r: Ranking) => !(
                            r.designConceptId === ranking.designConceptId
                            && r.filteringProductSpecId === ranking.filteringProductSpecId
                            && r.defaultProductSpecId === ranking.defaultProductSpecId
                            && r.rank === ranking.rank
                        ));
                    updateRanksByIndex(variant.rankings);
                }
            });
        });
    };

    const cloneVariant = (variantIndex: number): void => {
        setStrategyState((prev) => {
            if (!prev) return prev;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];
                const clone = {
                    name: uuidv4(),
                    cultures: [],
                    rankings: variant.rankings.map((r) => ({ ...r })),
                };

                draft.cultureVariantRankings.push(clone);
            });
        });
    };

    const updateBulkFilteringOptionGroup = (
        variantIndex: number,
        rankings: Ranking[],
        specId: string | null,
    ): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = variant.rankings.map((ranking) => {
                        const isSelected = rankings.some(
                            (selectedRanking) => areRankingsEqual(selectedRanking, ranking),
                        );

                        if (isSelected) {
                            return { ...ranking, filteringProductSpecId: specId };
                        }
                        return ranking;
                    });
                }
            });
        });
    };

    const updateBulkDefaultOptionGroup = (
        variantIndex: number,
        rankings: Ranking[],
        specId: string | null,
    ): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = variant.rankings.map((ranking) => {
                        const isSelected = rankings
                            .some((selectedRanking) => areRankingsEqual(selectedRanking, ranking));

                        if (isSelected) {
                            return { ...ranking, defaultProductSpecId: specId };
                        }
                        return ranking;
                    });
                }
            });
        });
    };

    const deleteBulkRankings = (variantIndex: number, rankings: Ranking[]): void => {
        setStrategyState((prev) => {
            if (!prev) return null;
            return produce(prev, (draft: Draft<Strategy>) => {
                const variant = draft.cultureVariantRankings[variantIndex];

                if (variant) {
                    variant.rankings = variant.rankings.filter((ranking) => !rankings
                        .some((selectedRanking) => areRankingsEqual(selectedRanking, ranking)));
                }
                updateRanksByIndex(variant.rankings);
            });
        });
    };

    return {
        strategy,
        setStrategy,
        updateReferenceProduct,
        updateName,
        updateRanking,
        addRank,
        deleteRank,
        updateVariantCultures,
        addVariant,
        deleteVariant,
        addOptionGroup,
        updateOptionGroup,
        deleteOptionGroup,
        hasUnsavedChanges,
        discardChanges,
        markAsSaved,
        importRanksFromCsv,
        addRankToVariant,
        deleteRankFromVariant,
        reorderRankings,
        cloneVariant,
        updateBulkFilteringOptionGroup,
        updateBulkDefaultOptionGroup,
        deleteBulkRankings,
    };
}
